import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/cn";

/* ─── Context ─── */

interface DropdownContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdown() {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error("Dropdown compound components must be used within <Dropdown>");
  return ctx;
}

/* ─── Root ─── */

interface DropdownProps {
  children: React.ReactNode;
}

function Dropdown({ children }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <DropdownContext.Provider
      value={{ open, setOpen, triggerRef, activeIndex, setActiveIndex }}
    >
      <div className="relative inline-block">{children}</div>
    </DropdownContext.Provider>
  );
}

/* ─── Trigger ─── */

interface DropdownTriggerProps {
  children: React.ReactNode;
  className?: string;
}

function DropdownTrigger({ children, className }: DropdownTriggerProps) {
  const { open, setOpen, triggerRef } = useDropdown();

  return (
    <button
      ref={triggerRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className={className}
    >
      {children}
    </button>
  );
}

/* ─── Menu ─── */

interface DropdownMenuProps {
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
}

function DropdownMenu({
  children,
  className,
  align = "start",
}: DropdownMenuProps) {
  const { open, setOpen, triggerRef, activeIndex, setActiveIndex } =
    useDropdown();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    minWidth: number;
  }>({ top: 0, left: 0, minWidth: 0 });

  const items = useRef<HTMLElement[]>([]);

  const collectItems = useCallback(() => {
    if (!menuRef.current) return;
    items.current = Array.from(
      menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'),
    );
  }, []);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4 + window.scrollY,
      left:
        align === "end"
          ? rect.right - (menuRef.current?.offsetWidth ?? rect.width) + window.scrollX
          : rect.left + window.scrollX,
      minWidth: rect.width,
    });
    setActiveIndex(-1);
    collectItems();
  }, [open, align, triggerRef, setActiveIndex, collectItems]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, setOpen, triggerRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const count = items.current.length;
      if (!count) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const next = activeIndex < count - 1 ? activeIndex + 1 : 0;
          setActiveIndex(next);
          items.current[next]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prev = activeIndex > 0 ? activeIndex - 1 : count - 1;
          setActiveIndex(prev);
          items.current[prev]?.focus();
          break;
        }
        case "Home": {
          e.preventDefault();
          setActiveIndex(0);
          items.current[0]?.focus();
          break;
        }
        case "End": {
          e.preventDefault();
          setActiveIndex(count - 1);
          items.current[count - 1]?.focus();
          break;
        }
        case "Escape":
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
          break;
      }
    },
    [activeIndex, setActiveIndex, setOpen, triggerRef],
  );

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          tabIndex={-1}
          initial={{ opacity: 0, scale: 0.95, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -4 }}
          transition={{ duration: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          onKeyDown={handleKeyDown}
          style={{
            position: "absolute",
            top: position.top,
            left: position.left,
            minWidth: position.minWidth,
          }}
          className={cn(
            "z-[40] overflow-y-auto max-h-[min(400px,70vh)]",
            "rounded-[var(--radius-xl)] p-1",
            "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-lg)]",
            "border border-[var(--border-hairline)]",
            "shadow-[var(--shadow-lg)]",
            className,
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* ─── Item ─── */

interface DropdownItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  destructive?: boolean;
}

function DropdownItem({
  children,
  icon,
  destructive = false,
  disabled,
  className,
  onClick,
  ...props
}: DropdownItemProps) {
  const { setOpen } = useDropdown();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(e);
    setOpen(false);
  };

  return (
    <button
      role="menuitem"
      tabIndex={-1}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-2 text-sm",
        "rounded-[var(--radius-md)]",
        "text-left select-none",
        "transition-colors duration-[var(--duration-fast)] ease-[var(--easing-default)]",
        "focus-visible:outline-none",
        destructive
          ? "text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10 focus:bg-[var(--color-soft-red)]/10"
          : "text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] focus:bg-[var(--bg-surface-hover)]",
        "disabled:opacity-40 disabled:pointer-events-none",
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            "shrink-0",
            destructive
              ? "text-[var(--color-soft-red)]"
              : "text-[var(--text-muted)]",
          )}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}

/* ─── Separator ─── */

function DropdownSeparator({ className }: { className?: string }) {
  return (
    <div
      role="separator"
      className={cn(
        "my-1 h-px bg-[var(--border-hairline)]",
        className,
      )}
    />
  );
}

/* ─── Label ─── */

function DropdownLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-3 py-1.5 text-xs font-medium text-[var(--text-muted)]",
        "select-none uppercase tracking-wider",
        className,
      )}
    >
      {children}
    </div>
  );
}

Dropdown.displayName = "Dropdown";
DropdownTrigger.displayName = "DropdownTrigger";
DropdownMenu.displayName = "DropdownMenu";
DropdownItem.displayName = "DropdownItem";
DropdownSeparator.displayName = "DropdownSeparator";
DropdownLabel.displayName = "DropdownLabel";

export {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
};
export type { DropdownProps, DropdownTriggerProps, DropdownMenuProps, DropdownItemProps };
